/**
 * exerciseCatalog.js
 *
 * Single source of truth for all micro-exercise data. Replaces the old
 * microExercisesData.js static list and the backend-driven capsule exercise
 * lookups. Both the library browse page and the capsule (mood-curated) flow
 * read from this file.
 *
 * Sections:
 *   1. Metadata    — categories, subcategories, moods
 *   2. Exercises   — full catalog with template config
 *   3. Lookups     — derived maps computed once at import time
 */

// ─── 1. Metadata ─────────────────────────────────────────────────────────────

export const CATEGORIES = [
  {
    id: "grounding",
    emoji: "🧘‍♀️",
    title: "Grounding & Nervous System Reset",
    subtitle:
      "Quick, science-backed exercises designed to calm your mind, reset your nervous system, and bring you back to the present moment.",
  },
];

export const SUBCATEGORIES = [
  { id: "breathwork", label: "Breathwork" },
  { id: "sensory", label: "Sensory Exercises" },
  { id: "somatic", label: "Somatic / Body-Based" },
  { id: "cognitive", label: "Cognitive / Thought-Based" },
  { id: "creative", label: "Creative Expression" },
  { id: "focus", label: "Focus & Grounding" },
];

export const MOODS = [
  { id: 2, name: "anxious", label: "Anxious", svg_icon: "/assets/moods/anxious.svg" },
  { id: 3, name: "calm", label: "Calm", svg_icon: "/assets/moods/calm.svg" },
  { id: 4, name: "creative", label: "Creative", svg_icon: "/assets/moods/creative.svg" },
  { id: 5, name: "critical", label: "Critical", svg_icon: "/assets/moods/critical.svg" },
  { id: 6, name: "energetic", label: "Energetic", svg_icon: "/assets/moods/energetic.svg" },
  { id: 7, name: "grounded", label: "Grounded", svg_icon: "/assets/moods/grounded.svg" },
  { id: 8, name: "scattered", label: "Scattered", svg_icon: "/assets/moods/scattered.svg" },
  { id: 9, name: "tense", label: "Tense", svg_icon: "/assets/moods/tense.svg" },
  { id: 10, name: "worried", label: "Worried", svg_icon: "/assets/moods/worried.svg" },
];

// ─── 2. Exercises ─────────────────────────────────────────────────────────────

export const exercises = [
  {
    id: "grounding-box-breath",
    title: "Box Breath",
    subtitle: "A simple four-phase breath cycle that calms the nervous system.",
    template: "breathing",
    category: "grounding",
    subcategory: "breathwork",
    moods: ["anxious", "tense", "worried"],
    duration: "1 min",
    image: "/images/micro-exercises/box-breathing.webp",
    config: {
      phases: [
        { name: "Inhale", duration: 4 },
        { name: "Hold", duration: 4 },
        { name: "Exhale", duration: 4 },
        { name: "Hold", duration: 4 },
      ],
      cycles: 4,
    },
  },
  {
    id: "grounding-vagus-hum",
    title: "Vagus Nerve Hum",
    subtitle: "Activate your vagus nerve with a slow inhale and extended hum.",
    template: "breathing",
    category: "grounding",
    subcategory: "breathwork",
    moods: ["tense", "anxious"],
    duration: "2 min",
    image: "/images/micro-exercises/vagus-hum.webp",
    config: {
      phases: [
        { name: "Inhale", duration: 4 },
        { name: "Hum", duration: 8 },
      ],
      cycles: 5,
    },
  },
  {
    id: "grounding-sensory-5421",
    title: "5-4-3-2-1 Sensory Grounding",
    subtitle: "Anchor yourself to the present by engaging all five senses.",
    template: "grounding",
    category: "grounding",
    subcategory: "sensory",
    moods: ["anxious", "scattered", "worried"],
    duration: "3 min",
    image: "/images/micro-exercises/sensory-grounding.webp",
    config: {},
  },
  {
    id: "grounding-name-three",
    title: "Name 3 Things",
    subtitle: "A quick sensory check-in to interrupt anxious thought loops.",
    template: "grounding",
    category: "grounding",
    subcategory: "sensory",
    moods: ["anxious", "scattered"],
    duration: "1 min",
    image: "/images/micro-exercises/name-three-things.webp",
    config: {
      prompts: [
        {
          count: 3,
          sense: "see",
          hint: "Look around and name three things you can see.",
        },
        {
          count: 3,
          sense: "touch",
          hint: "Notice three things you can physically feel.",
        },
        {
          count: 3,
          sense: "hear",
          hint: "Listen for three distinct sounds.",
        },
      ],
    },
  },
  {
    id: "grounding-body-release",
    title: "Progressive Body Release",
    subtitle: "Systematically tense and release muscle groups to melt tension.",
    template: "pmr",
    category: "grounding",
    subcategory: "somatic",
    moods: ["tense", "energetic"],
    duration: "3 min",
    image: "/images/micro-exercises/body-exercise.webp",
    config: {
      steps: [
        { label: "Hands & Forearms", region: "arms",  tense: "Clench your fists tightly — hold...",         release: "Slowly release your hands — let go..." },
        { label: "Upper Arms",       region: "arms",  tense: "Flex your biceps — hold...",                  release: "Release your arms — feel them go heavy..." },
        { label: "Shoulders",        region: "torso", tense: "Raise your shoulders to your ears — hold...", release: "Drop your shoulders — let them melt down..." },
        { label: "Chest & Stomach",  region: "torso", tense: "Tighten your stomach — hold...",              release: "Release your core — breathe softly..." },
        { label: "Legs & Thighs",    region: "legs",  tense: "Press your thighs together — hold...",        release: "Release your legs — let them fall open..." },
        { label: "Feet",             region: "legs",  tense: "Curl your toes tightly — hold...",            release: "Release your feet — spread your toes..." },
      ],
    },
  },
  {
    id: "grounding-panic-anchor",
    title: "Panic Anchor",
    subtitle: "A five-phase funnel that walks you down from a panic peak.",
    template: "focus_anchoring",
    category: "grounding",
    subcategory: "focus",
    moods: ["anxious", "worried", "scattered"],
    duration: "3 min",
    image: "/images/micro-exercises/panic-anchor.webp",
    config: {},
  },
  {
    id: "grounding-slow-down-list",
    title: "Slow Down List",
    subtitle: "Empty your racing mind by writing one thought at a time.",
    template: "journaling",
    category: "grounding",
    subcategory: "cognitive",
    moods: ["scattered", "worried", "anxious"],
    duration: "2 min",
    image: "/images/micro-exercises/slow-down-list.webp",
    config: {
      prompt:
        "Write down what\u2019s racing through your mind, one thing at a time\u2026",
      duration: 120,
    },
  },

  // ── Plug-and-play exercises (data only, no new components) ───────────

  {
    id: "breathwork-478",
    title: "4-7-8 Calm Breath",
    subtitle:
      "A navy-tested pattern to slow your nervous system in 60 seconds.",
    template: "breathing",
    category: "grounding",
    subcategory: "breathwork",
    moods: ["anxious", "tense", "worried"],
    duration: "2 min",
    image: "/images/micro-exercises/box-breathing.webp",
    config: {
      phases: [
        { name: "Inhale", duration: 4 },
        { name: "Hold", duration: 7 },
        { name: "Exhale", duration: 8 },
      ],
      cycles: 4,
    },
  },
  {
    id: "visualization-safe-place",
    title: "Safe Place Visualization",
    subtitle: "Build a mental refuge you can return to anytime.",
    template: "visualization",
    category: "grounding",
    subcategory: "cognitive",
    moods: ["anxious", "worried", "calm"],
    duration: "2 min",
    image: "/images/micro-exercises/body-exercise.webp",
    config: {
      scenes: [
        "Close your eyes. Imagine a place where you feel completely safe. It can be real or imagined.",
        "Look around this place. Notice the colors, the light, the shapes. What do you see?",
        "Now notice what you hear. Perhaps water, wind, birds, or gentle silence.",
        "Feel the ground beneath you. Notice the temperature on your skin. You are held here.",
        "Take one deep breath. This place is yours. You can return anytime you need it.",
      ],
      pace: 10,
    },
  },
  {
    id: "creative-emotional-color",
    title: "Emotional Color Map",
    subtitle: "Describe your feelings using only colors and textures.",
    template: "creative_expression",
    category: "grounding",
    subcategory: "creative",
    moods: ["critical", "creative", "grounded"],
    duration: "3 min",
    image: "/images/micro-exercises/slow-down-list.webp",
    config: {
      prompt:
        "If your current emotion were a color, what would it be? Describe its texture, temperature, and movement. Write freely \u2014 there are no wrong answers.",
      duration: 180,
    },
  },

  // ── calm ─────────────────────────────────────────────────────────────

  {
    id: "calm-three-good-things",
    title: "Three Good Things",
    subtitle: "Notice what went right, even in a small way.",
    template: "journaling",
    category: "grounding",
    subcategory: "cognitive",
    moods: ["calm"],
    duration: "2 min",
    image: "/images/micro-exercises/slow-down-list.webp",
    config: {
      prompt:
        "Think about your day. Write down three things that went well, no matter how small. A warm coffee, a kind word, a moment of quiet.",
      duration: 120,
    },
  },
  {
    id: "calm-savoring-breath",
    title: "Savoring Breath",
    subtitle: "Slow your exhale to stay in this moment a little longer.",
    template: "breathing",
    category: "grounding",
    subcategory: "breathwork",
    moods: ["calm"],
    duration: "1 min",
    image: "/images/micro-exercises/box-breathing.webp",
    config: {
      phases: [
        { name: "Inhale", duration: 4 },
        { name: "Hold", duration: 2 },
        { name: "Exhale", duration: 6 },
      ],
      cycles: 5,
    },
  },
  {
    id: "calm-soft-body-scan",
    title: "Soft Body Scan",
    subtitle: "A gentle check-in from head to toe \u2014 noticing ease, not tension.",
    template: "visualization",
    category: "grounding",
    subcategory: "somatic",
    moods: ["calm"],
    duration: "2 min",
    image: "/images/micro-exercises/body-exercise.webp",
    config: {
      scenes: [
        "Close your eyes and take one slow breath. Let your body settle into wherever you\u2019re sitting or lying.",
        "Bring your attention to the top of your head. Notice whatever is there \u2014 warmth, softness, stillness. No need to change it.",
        "Gently move your awareness down through your neck and shoulders. Are they holding anything? Let them be as they are.",
        "Notice your chest and belly rising and falling with each breath. You don\u2019t need to control it. Just feel it.",
        "Bring your attention down through your legs, your feet, your toes. You are fully here, fully held.",
        "Take one last breath. Rest in the quiet for a moment before you return.",
      ],
      pace: 12,
    },
  },

  // ── grounded ─────────────────────────────────────────────────────────

  {
    id: "grounded-what-feels-solid",
    title: "What Feels Solid",
    subtitle: "A short reflection on what\u2019s holding steady in your life.",
    template: "journaling",
    category: "grounding",
    subcategory: "cognitive",
    moods: ["grounded"],
    duration: "2 min",
    image: "/images/micro-exercises/slow-down-list.webp",
    config: {
      prompt:
        "What in your life right now feels stable? It doesn\u2019t need to be big. Write about what is holding steady \u2014 a routine, a person, a value, a small certainty.",
      duration: 120,
    },
  },
  {
    id: "grounded-roots-visualization",
    title: "Roots Visualization",
    subtitle: "Imagine yourself rooted \u2014 steady, connected, held.",
    template: "visualization",
    category: "grounding",
    subcategory: "cognitive",
    moods: ["grounded"],
    duration: "2 min",
    image: "/images/micro-exercises/body-exercise.webp",
    config: {
      scenes: [
        "Sit comfortably and feel the ground beneath you. Notice where your body makes contact with the surface.",
        "Imagine roots growing downward from wherever you\u2019re sitting \u2014 slow, quiet, reaching deep into the earth.",
        "With every exhale, those roots grow a little deeper. You don\u2019t need to try. Just breathe.",
        "Feel the weight of being held. The ground is not going anywhere. Neither are you.",
        "Take a breath and carry this steadiness with you. You can return to it anytime.",
      ],
      pace: 12,
    },
  },
  {
    id: "grounded-mindful-observation",
    title: "Mindful Observation",
    subtitle: "Choose one object and give it your full, unhurried attention.",
    template: "grounding",
    category: "grounding",
    subcategory: "sensory",
    moods: ["grounded"],
    duration: "2 min",
    image: "/images/micro-exercises/sensory-grounding.webp",
    config: {
      prompts: [
        {
          count: 1,
          sense: "see",
          hint: "Find one object near you. Look at it as if you\u2019ve never seen it before. Notice its shape, color, and edges.",
        },
        {
          count: 1,
          sense: "touch",
          hint: "Pick it up if you can. Notice its weight, temperature, and texture without labeling it \u2014 just feel.",
        },
        {
          count: 1,
          sense: "notice",
          hint: "What details keep revealing themselves? Stay with it a little longer than feels necessary.",
        },
      ],
    },
  },

  // ── creative ─────────────────────────────────────────────────────────

  {
    id: "creative-first-thought",
    title: "First Thought",
    subtitle: "Write without editing. Let what\u2019s inside come out.",
    template: "journaling",
    category: "grounding",
    subcategory: "cognitive",
    moods: ["creative"],
    duration: "2 min",
    image: "/images/micro-exercises/slow-down-list.webp",
    config: {
      prompt:
        "Don\u2019t plan, don\u2019t edit, don\u2019t cross anything out. Start writing and keep going. The first thought leads to the next. Follow it.",
      duration: 120,
    },
  },
  {
    id: "creative-shape-of-a-feeling",
    title: "Shape of a Feeling",
    subtitle: "Describe your creative energy as a shape or texture \u2014 no wrong answers.",
    template: "creative_expression",
    category: "grounding",
    subcategory: "creative",
    moods: ["creative"],
    duration: "3 min",
    image: "/images/micro-exercises/slow-down-list.webp",
    config: {
      prompt:
        "Your creative energy has a shape right now. Maybe it\u2019s sharp, coiled, scattered, or humming. Describe it: its texture, color, temperature, and how it moves. There are no wrong answers.",
      duration: 180,
    },
  },
  {
    id: "creative-one-true-sentence",
    title: "One True Sentence",
    subtitle: "Find the one sentence that says exactly where you are.",
    template: "journaling",
    category: "grounding",
    subcategory: "cognitive",
    moods: ["creative"],
    duration: "2 min",
    image: "/images/micro-exercises/slow-down-list.webp",
    config: {
      prompt:
        "Write one sentence that honestly captures where you are right now. Not where you wish you were \u2014 where you are. Keep rewriting it until it\u2019s exactly right.",
      duration: 120,
    },
  },

  // ── critical ─────────────────────────────────────────────────────────

  {
    id: "critical-kinder-voice",
    title: "The Kinder Voice",
    subtitle: "Say to yourself what you\u2019d say to someone you love.",
    template: "journaling",
    category: "grounding",
    subcategory: "cognitive",
    moods: ["critical"],
    duration: "2 min",
    image: "/images/micro-exercises/slow-down-list.webp",
    config: {
      prompt:
        "Think of something you\u2019ve been criticizing yourself for. Now write what you\u2019d say to a close friend going through exactly the same thing.",
      duration: 120,
    },
  },
  {
    id: "critical-fact-or-story",
    title: "Fact or Story",
    subtitle: "Separate what actually happened from what your inner critic added.",
    template: "journaling",
    category: "grounding",
    subcategory: "cognitive",
    moods: ["critical"],
    duration: "2 min",
    image: "/images/micro-exercises/slow-down-list.webp",
    config: {
      prompt:
        "Write down a thought your inner critic keeps repeating. Then separate it: what actually happened (just the facts), and what your mind added on top of it.",
      duration: 120,
    },
  },
  {
    id: "critical-inner-ally",
    title: "Inner Ally",
    subtitle: "Meet the part of you that\u2019s been on your side all along.",
    template: "visualization",
    category: "grounding",
    subcategory: "cognitive",
    moods: ["critical"],
    duration: "2 min",
    image: "/images/micro-exercises/body-exercise.webp",
    config: {
      scenes: [
        "Close your eyes. Take a slow breath. You\u2019re going to meet a part of yourself that doesn\u2019t criticize \u2014 one that has always been quietly on your side.",
        "Imagine this part of you has a presence \u2014 maybe a warmth, a calm voice, or a feeling of steadiness. It has been here the whole time.",
        "Let it speak. What would it say to you right now, about the thing you\u2019ve been hardest on yourself about?",
        "Listen without arguing. Let the words land. You don\u2019t have to believe them fully yet \u2014 just let them in.",
        "Take a breath. This voice is yours. It will be here the next time the critic gets loud.",
      ],
      pace: 12,
    },
  },

  // ── energetic ────────────────────────────────────────────────────────

  {
    id: "energetic-channel-the-charge",
    title: "Channel the Charge",
    subtitle: "Your energy wants to go somewhere. Let\u2019s find out where.",
    template: "journaling",
    category: "grounding",
    subcategory: "cognitive",
    moods: ["energetic"],
    duration: "2 min",
    image: "/images/micro-exercises/slow-down-list.webp",
    config: {
      prompt:
        "Your energy is high. Where does it want to go? What is it really about? Write quickly and honestly \u2014 don\u2019t filter. Let the charge speak.",
      duration: 120,
    },
  },
  {
    id: "energetic-directing-breath",
    title: "Directing Breath",
    subtitle: "A short pattern that sharpens focus without dampening your energy.",
    template: "breathing",
    category: "grounding",
    subcategory: "breathwork",
    moods: ["energetic"],
    duration: "1 min",
    image: "/images/micro-exercises/box-breathing.webp",
    config: {
      phases: [
        { name: "Inhale", duration: 4 },
        { name: "Exhale", duration: 4 },
      ],
      cycles: 6,
    },
  },
  {
    id: "energetic-one-point-of-focus",
    title: "One Point of Focus",
    subtitle: "Choose one thing. Give it everything for the next few minutes.",
    template: "focus_anchoring",
    category: "grounding",
    subcategory: "focus",
    moods: ["energetic"],
    duration: "3 min",
    image: "/images/micro-exercises/panic-anchor.webp",
    config: {
      steps: [
        { label: "Choose your focus", instruction: "Take a breath and decide: what is the one thing that needs your attention most right now?" },
        { label: "Name it", instruction: "Write it down or say it out loud. Name it specifically \u2014 not \u2018work\u2019, but the actual thing." },
        { label: "Clear the field", instruction: "Close everything else. If a distraction comes up, notice it, let it go, return here." },
        { label: "Begin", instruction: "Give this one thing your full, undivided attention. Nothing else exists right now." },
        { label: "Complete", instruction: "You focused. Notice how that feels \u2014 even for a few minutes, you directed your energy on purpose." },
      ],
    },
  },
];

// ─── 3. Derived Lookups ───────────────────────────────────────────────────────

export const exerciseById = Object.fromEntries(
  exercises.map((ex) => [ex.id, ex]),
);

export const exercisesByCategory = exercises.reduce((acc, ex) => {
  (acc[ex.category] ||= []).push(ex);
  return acc;
}, {});

export const exercisesByMood = exercises.reduce((acc, ex) => {
  ex.moods.forEach((mood) => {
    (acc[mood] ||= []).push(ex);
  });
  return acc;
}, {});
